/* ==========================================================================
   Lidya Concept — theme behaviours
   Vanilla ES2017+. No framework, no build step.
   Every component is a custom element or a delegated handler so that Theme
   Editor section re-renders (shopify:section:load) re-initialise cleanly.
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.themeRoutes || {};
  var strings = window.themeStrings || {};
  var config = window.themeConfig || {};

  /* ---------------------------------------------------------------- utils */

  function on(el, type, handler, opts) {
    if (el) el.addEventListener(type, handler, opts);
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var format = config.moneyFormat || '{{amount}}';
    var value = whole + ',' + parts[1];
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  /** Real viewport height for full-height drawers on mobile browsers. */
  function setViewportUnit() {
    document.documentElement.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
  }
  setViewportUnit();
  on(window, 'resize', setViewportUnit);

  var scrollLockCount = 0;
  function lockScroll(lock) {
    scrollLockCount = Math.max(0, scrollLockCount + (lock ? 1 : -1));
    document.body.classList.toggle('is-locked', scrollLockCount > 0);
  }

  function trapFocus(container, event) {
    var focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------- overlay */

  var overlay = document.querySelector('[data-page-overlay]');
  function showOverlay(show) {
    if (!overlay) return;
    overlay.hidden = false;
    overlay.classList.toggle('is-active', show);
    if (!show) {
      window.setTimeout(function () {
        if (!document.querySelector('.drawer.is-open')) overlay.hidden = true;
      }, 500);
    }
  }

  /* ================================================================ drawer */

  var Drawer = {
    open: function (id, opener) {
      var el = document.getElementById(id);
      if (!el) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      el.dataset.opener = opener && opener.id ? opener.id : '';
      if (opener) opener.setAttribute('aria-expanded', 'true');
      showOverlay(true);
      lockScroll(true);
      var focusable = el.querySelector('[data-drawer-focus]') || el.querySelector('button, a[href]');
      if (focusable) window.setTimeout(function () { focusable.focus(); }, 60);
      document.dispatchEvent(new CustomEvent('theme:drawer:open', { detail: { id: id } }));
    },
    close: function (el) {
      if (!el) el = document.querySelector('.drawer.is-open');
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (!document.querySelector('.drawer.is-open')) showOverlay(false);
      var opener = el.dataset.opener ? document.getElementById(el.dataset.opener) : null;
      if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus(); }
      document.dispatchEvent(new CustomEvent('theme:drawer:close', { detail: { id: el.id } }));
    },
    closeAll: function () {
      document.querySelectorAll('.drawer.is-open').forEach(function (el) { Drawer.close(el); });
    }
  };
  window.themeDrawer = Drawer;

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Drawer.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    var closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      Drawer.close(closer.closest('.drawer'));
    }
  });

  on(overlay, 'click', function () { Drawer.closeAll(); });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      var openDrawer = document.querySelector('.drawer.is-open');
      if (openDrawer) { Drawer.close(openDrawer); return; }
      var openSearch = document.querySelector('[data-predictive]:not([hidden])');
      if (openSearch) openSearch.hidden = true;
    }
    if (event.key === 'Tab') {
      var drawer = document.querySelector('.drawer.is-open');
      if (drawer) trapFocus(drawer, event);
    }
  });

  /* ========================================================= sticky header */
  /* Mirrors the reference: a hard `pageYOffset > 200` threshold, bound only
     above 768px, with the placeholder height pinned so the page cannot jump. */

  var STICKY_OFFSET = 200;
  var DESKTOP = 768;

  function initStickyHeader() {
    var holder = document.querySelector('[data-header-holder]');
    var header = document.querySelector('[data-header]');
    if (!holder || !header || holder.dataset.stickyEnabled === 'false') return;

    var attached = false;
    var naturalHeight = 0;

    function measure() {
      if (header.classList.contains('header--sticky')) return;
      naturalHeight = header.getBoundingClientRect().height;
    }

    function apply() {
      var sticky = window.pageYOffset > STICKY_OFFSET;
      header.classList.toggle('header--sticky', sticky);
      var nav = document.querySelector('[data-main-nav]');
      if (nav) nav.classList.toggle('nav--sticky', sticky);
      holder.style.height = sticky && naturalHeight ? naturalHeight + 'px' : 'auto';
      if (!sticky) header.classList.remove('header--search-open');
    }

    function sync() {
      if (window.innerWidth > DESKTOP) {
        if (!attached) {
          window.addEventListener('scroll', apply, { passive: true });
          attached = true;
        }
        measure();
        apply();
      } else if (attached) {
        window.removeEventListener('scroll', apply);
        attached = false;
        header.classList.remove('header--sticky');
        holder.style.height = 'auto';
      }
    }

    measure();
    sync();
    window.addEventListener('resize', function () { measure(); sync(); });

    // Sticky search toggle swaps the compact nav back out for the field.
    var toggle = document.querySelector('[data-header-search-toggle]');
    on(toggle, 'click', function () {
      header.classList.toggle('header--search-open');
      if (header.classList.contains('header--search-open')) {
        var input = header.querySelector('[data-search-input]');
        if (input) input.focus();
      }
    });
  }

  /* ==================================================== mobile navigation */

  function initMobileNav() {
    var drawer = document.getElementById('MobileNav');
    if (!drawer) return;

    drawer.addEventListener('click', function (event) {
      var openBtn = event.target.closest('[data-mobile-panel-open]');
      if (openBtn) {
        var panel = drawer.querySelector('#' + openBtn.getAttribute('data-mobile-panel-open'));
        if (panel) {
          panel.classList.add('is-open');
          openBtn.setAttribute('aria-expanded', 'true');
          var heading = panel.querySelector('.mobile-nav__panel-title');
          if (heading) heading.setAttribute('tabindex', '-1'), heading.focus();
        }
        return;
      }
      var backBtn = event.target.closest('[data-mobile-panel-back]');
      if (backBtn) {
        var open = backBtn.closest('.mobile-nav__panel');
        if (open) {
          open.classList.remove('is-open');
          var opener = drawer.querySelector('[data-mobile-panel-open="' + open.id + '"]');
          if (opener) opener.setAttribute('aria-expanded', 'false');
        }
      }
    });

    // Reset to the root level once the drawer has finished closing.
    document.addEventListener('theme:drawer:close', function (event) {
      if (event.detail.id !== 'MobileNav') return;
      window.setTimeout(function () {
        drawer.querySelectorAll('.mobile-nav__panel.is-open').forEach(function (p) {
          p.classList.remove('is-open');
          var opener = drawer.querySelector('[data-mobile-panel-open="' + p.id + '"]');
          if (opener) opener.setAttribute('aria-expanded', 'false');
        });
      }, 500);
    });
  }

  /* ============================================================ hero slider */

  function initHeroSliders(scope) {
    (scope || document).querySelectorAll('[data-hero]').forEach(function (hero) {
      if (hero.dataset.initialised === 'true') return;
      hero.dataset.initialised = 'true';

      var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-slide]'));
      if (slides.length < 2) {
        // Still let a lone video slide play.
        var solo = hero.querySelector('video');
        if (solo) solo.play().catch(function () {});
        return;
      }

      var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-dot]'));
      var interval = parseInt(hero.dataset.interval, 10) || 5000;
      var autoplay = hero.dataset.autoplay !== 'false';
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var index = 0;
      var timer = null;
      var track = hero.querySelector('.hero__track');

      function clear() { if (timer) { window.clearTimeout(timer); timer = null; } }

      // Mobile only: give .hero__track the active slide's own real image
      // ratio (data-mobile-ratio, from image_mobile.aspect_ratio — no crop,
      // no zoom, no forced text-fit height: the box is exactly the image's
      // shape, full stop). .hero__track already has a definite `width: 100%`
      // (see theme.css), so setting aspect-ratio here makes the browser
      // derive height from that width on its own — nothing is measured or
      // computed by hand. Desktop keeps its own fixed --hero-ratio box
      // untouched — this clears the mobile override the moment the
      // viewport reaches that breakpoint.
      // Applied instantly (no CSS transition): animating aspect-ratio while
      // reassigning it on every slide change proved unreliable — the box
      // would sometimes keep the previous slide's ratio instead of moving
      // to the new one — so correctness (every slide gets its own exact
      // ratio) wins over a smooth resize here.
      function sizeHero() {
        if (!track || window.innerWidth >= 768) {
          if (track) track.style.aspectRatio = '';
          return;
        }
        track.style.aspectRatio = parseFloat(slides[index].dataset.mobileRatio) || 1.5;
      }

      function schedule() {
        clear();
        if (!autoplay || reduced) return;
        if (slides[index].querySelector('video')) return; // video drives its own advance
        timer = window.setTimeout(function () { go(index + 1); }, interval);
      }

      function go(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          slide.querySelectorAll('a').forEach(function (a) {
            a.tabIndex = active ? 0 : -1;
          });
          var video = slide.querySelector('video');
          if (video && !active) video.pause();
        });
        sizeHero();
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });

        var current = slides[index].querySelector('video');
        if (current) {
          current.currentTime = 0;
          var playing = current.play();
          if (playing && playing.catch) playing.catch(function () { schedule(); });
          current.onended = function () { go(index + 1); };
        } else {
          schedule();
        }
      }

      hero.querySelectorAll('[data-hero-prev]').forEach(function (b) {
        on(b, 'click', function () { go(index - 1); });
      });
      hero.querySelectorAll('[data-hero-next]').forEach(function (b) {
        on(b, 'click', function () { go(index + 1); });
      });
      dots.forEach(function (dot, i) {
        on(dot, 'click', function () { go(i); });
      });

      // Pause while off-screen or on a hidden tab.
      on(document, 'visibilitychange', function () {
        if (document.hidden) clear(); else schedule();
      });
      on(window, 'resize', sizeHero);

      sizeHero();
      go(0);
    });
  }

  /* ================================================================= rails */

  function initRails(scope) {
    (scope || document).querySelectorAll('[data-rail]').forEach(function (rail) {
      if (rail.dataset.initialised === 'true') return;
      rail.dataset.initialised = 'true';

      var wrap = rail.closest('[data-rail-wrap]') || rail.parentElement;
      var prev = wrap ? wrap.querySelector('[data-rail-prev]') : null;
      var next = wrap ? wrap.querySelector('[data-rail-next]') : null;
      var loop = rail.dataset.loop === 'true';

      function step() {
        var first = rail.children[0];
        var second = rail.children[1];
        if (!first) return rail.clientWidth;
        // Use the real pitch so the gutter is included at every breakpoint.
        if (second) return second.offsetLeft - first.offsetLeft;
        return first.getBoundingClientRect().width;
      }

      function atStart() { return rail.scrollLeft <= 2; }
      function atEnd() { return rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2; }

      function update() {
        if (loop) return;
        if (prev) prev.disabled = atStart();
        if (next) next.disabled = atEnd();
      }

      on(prev, 'click', function () {
        if (loop && atStart()) { rail.scrollTo({ left: rail.scrollWidth, behavior: 'smooth' }); return; }
        rail.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      on(next, 'click', function () {
        if (loop && atEnd()) { rail.scrollTo({ left: 0, behavior: 'smooth' }); return; }
        rail.scrollBy({ left: step(), behavior: 'smooth' });
      });
      on(rail, 'scroll', update, { passive: true });
      on(window, 'resize', update);
      update();
    });
  }

  /* ============================================================= hotspots */

  function initHotspots(scope) {
    (scope || document).querySelectorAll('[data-hotspot]').forEach(function (spot) {
      var dot = spot.querySelector('[data-hotspot-dot]');
      on(dot, 'click', function (event) {
        // Desktop reveals on hover via CSS; the click toggle is for touch.
        if (window.innerWidth >= DESKTOP) return;
        event.preventDefault();
        var isOpen = spot.classList.contains('is-open');
        (scope || document).querySelectorAll('[data-hotspot].is-open').forEach(function (s) {
          s.classList.remove('is-open');
        });
        spot.classList.toggle('is-open', !isOpen);
        dot.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ======================================================= product gallery */

  function initGalleries(scope) {
    (scope || document).querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var main = gallery.querySelector('[data-gallery-main]');
      gallery.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb || !main) return;
        event.preventDefault();
        var src = thumb.getAttribute('data-media-src');
        var srcset = thumb.getAttribute('data-media-srcset');
        var img = main.querySelector('img');
        if (img && src) {
          img.src = src;
          if (srcset) img.srcset = srcset;
        }
        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------------------------------------- product gallery video fullscreen */

  // CSS `:fullscreen`/`:-webkit-full-screen` (see main-product.liquid) covers
  // most browsers, but plenty of Android browsers/WebViews (Samsung Internet,
  // older Chrome/WebView builds, various OEM browsers) put a DIFFERENT
  // ancestor into fullscreen rather than the <video> itself, or skip the
  // standard Fullscreen API for video controls entirely — in both cases the
  // CSS pseudo-class never matches the video, so the square thumbnail
  // crop (aspect-ratio: 1/1; object-fit: cover) stays applied at fullscreen
  // size. This listens for every fullscreen-related event (standard, all
  // vendor-prefixed variants, and iOS/Android's own video-specific
  // webkitbegin/endfullscreen) and forces the fit via inline style — which
  // always wins regardless of which element the browser calls "fullscreen".
  function initProductVideoFullscreenFix(scope) {
    (scope || document).querySelectorAll('.product__thumb--media video').forEach(function (video) {
      if (video.dataset.fsFixInit === 'true') return;
      video.dataset.fsFixInit = 'true';

      function fit() {
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.aspectRatio = 'auto';
        video.style.objectFit = 'contain';
        video.style.background = '#000';
      }
      function unfit() {
        video.style.width = '';
        video.style.height = '';
        video.style.aspectRatio = '';
        video.style.objectFit = '';
        video.style.background = '';
      }
      function isThisVideoFullscreen() {
        var fsEl = document.fullscreenElement || document.webkitFullscreenElement ||
          document.mozFullScreenElement || document.msFullscreenElement;
        return !!fsEl && (fsEl === video || fsEl.contains(video));
      }

      ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach(function (evt) {
        on(document, evt, function () {
          if (isThisVideoFullscreen()) fit(); else unfit();
        });
      });
      // iOS's (and some Android WebViews') own native inline-video fullscreen —
      // fires directly on the <video>, no document.*FullscreenElement involved.
      on(video, 'webkitbeginfullscreen', fit);
      on(video, 'webkitendfullscreen', unfit);
    });
  }

  /* ================================================== variant selection */

  function initVariantPickers(scope) {
    (scope || document).querySelectorAll('[data-variant-picker]').forEach(function (picker) {
      if (picker.dataset.initialised === 'true') return;
      picker.dataset.initialised = 'true';

      var form = picker.closest('form') || document.querySelector('[data-product-form]');
      var dataEl = document.querySelector('#' + picker.dataset.variantData);
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      var idInput = form ? form.querySelector('[data-variant-id]') : null;
      var priceEl = document.querySelector('[data-product-price]');
      var submit = form ? form.querySelector('[data-add-to-cart]') : null;

      function selectedOptions() {
        return Array.prototype.map.call(
          picker.querySelectorAll('[data-option-index]'),
          function (group) {
            var checked = group.querySelector('input:checked');
            return checked ? checked.value : null;
          }
        );
      }

      function update() {
        var chosen = selectedOptions();
        var match = variants.find(function (variant) {
          return variant.options.every(function (opt, i) { return opt === chosen[i]; });
        });

        if (idInput) idInput.value = match ? match.id : '';

        if (submit) {
          if (!match) {
            submit.disabled = true;
            submit.textContent = strings.unavailable || 'Unavailable';
          } else if (!match.available) {
            submit.disabled = true;
            submit.textContent = strings.soldOut || 'Sold out';
          } else {
            submit.disabled = false;
            submit.textContent = strings.addToCart || 'Add to cart';
          }
        }

        if (match && priceEl) {
          var html = '<span class="price__current">' + formatMoney(match.price) + '</span>';
          if (match.compare_at_price && match.compare_at_price > match.price) {
            html += ' <s class="price__compare">' + formatMoney(match.compare_at_price) + '</s>';
          }
          priceEl.innerHTML = html;
          priceEl.classList.toggle(
            'price--on-sale',
            !!(match.compare_at_price && match.compare_at_price > match.price)
          );
        }

        if (match && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url.toString());
        }

        // Grey out option values that cannot be reached.
        picker.querySelectorAll('[data-option-index]').forEach(function (group, groupIndex) {
          group.querySelectorAll('input').forEach(function (input) {
            var probe = chosen.slice();
            probe[groupIndex] = input.value;
            var exists = variants.some(function (v) {
              return v.available && v.options.every(function (opt, i) {
                return i === groupIndex ? opt === probe[i] : opt === probe[i];
              });
            });
            input.closest('.variant-pill').classList.toggle('is-unavailable', !exists);
          });
        });
      }

      picker.addEventListener('change', update);
      update();
    });
  }

  /* ================================================================= cart */

  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function refreshCartUI() {
    return fetch(routes.cart + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-content]');
        var current = document.querySelector('[data-cart-drawer-content]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        return fetch(routes.cart + '.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
  }
  window.themeRefreshCart = refreshCartUI;

  function addToCart(id, quantity, button) {
    if (button) button.classList.add('is-loading');
    return cartRequest(routes.cartAdd, { items: [{ id: id, quantity: quantity || 1 }] })
      .then(function () { return refreshCartUI(); })
      .then(function () {
        if (config.cartType === 'drawer') Drawer.open('CartDrawer', button);
        else window.location.href = routes.cart;
      })
      .catch(function (err) {
        var message = (err && err.description) || strings.cartError;
        window.alert(message);
      })
      .finally(function () {
        if (button) button.classList.remove('is-loading');
      });
  }

  // Quick add from product cards.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    var id = button.getAttribute('data-variant-id');
    if (!id) {
      // Product has options that must be chosen on the product page.
      var href = button.getAttribute('data-product-url');
      if (href) window.location.href = href;
      return;
    }
    addToCart(id, 1, button);
  });

  // Product form submit.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-product-form]');
    if (!form) return;
    if (config.cartType !== 'drawer') return; // let it post normally to /cart/add
    event.preventDefault();
    var id = form.querySelector('[data-variant-id]');
    var qty = form.querySelector('[name="quantity"]');
    var button = form.querySelector('[data-add-to-cart]');
    if (!id || !id.value) return;
    addToCart(id.value, qty ? parseInt(qty.value, 10) : 1, button);
  });

  // Cart line quantity + removal (drawer and cart page).
  document.addEventListener('click', function (event) {
    var stepper = event.target.closest('[data-qty-change]');
    if (stepper) {
      event.preventDefault();
      var input = stepper.parentElement.querySelector('input');
      if (!input) return;
      var delta = parseInt(stepper.getAttribute('data-qty-change'), 10);
      var value = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 0) + delta);
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      updateLine(remove.getAttribute('data-line'), 0);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    updateLine(input.getAttribute('data-line'), parseInt(input.value, 10) || 0);
  });

  function updateLine(line, quantity) {
    if (!line) return;
    cartRequest(routes.cartChange, { line: parseInt(line, 10), quantity: quantity })
      .then(function () {
        if (document.body.classList.contains('template-cart')) window.location.reload();
        else refreshCartUI();
      })
      .catch(function () { window.alert(strings.cartError); });
  }

  /* =================================================== predictive search */

  function initPredictiveSearch(scope) {
    (scope || document).querySelectorAll('[data-search-form]').forEach(function (form) {
      if (form.dataset.initialised === 'true') return;
      form.dataset.initialised = 'true';

      var input = form.querySelector('[data-search-input]');
      var panel = form.parentElement.querySelector('[data-predictive]');
      if (!input || !panel) return;

      var controller = null;
      var debounce = null;

      function close() { panel.hidden = true; }

      function query(term) {
        if (controller) controller.abort();
        controller = new AbortController();
        var url =
          routes.predictiveSearch +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,collection,page,article' +
          '&resources[limit]=6' +
          '&section_id=predictive-search';
        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var results = doc.querySelector('[data-predictive-results]');
            if (results) {
              panel.innerHTML = results.innerHTML;
              panel.hidden = false;
            }
          })
          .catch(function () { /* aborted */ });
      }

      on(input, 'input', function () {
        var term = input.value.trim();
        window.clearTimeout(debounce);
        if (term.length < 2) {
          // Show the default panel (popular searches / categories) again.
          var fallback = panel.querySelector('[data-predictive-default]');
          if (fallback) { panel.hidden = false; } else { close(); }
          return;
        }
        debounce = window.setTimeout(function () { query(term); }, 250);
      });

      on(input, 'focus', function () { panel.hidden = false; });

      document.addEventListener('mousedown', function (event) {
        if (!form.parentElement.contains(event.target)) close();
      });
      on(form, 'focusout', function (event) {
        if (!event.relatedTarget || !form.parentElement.contains(event.relatedTarget)) {
          window.setTimeout(close, 120);
        }
      });
    });
  }

  /* ================================================================ facets */

  function initFacets(scope) {
    (scope || document).querySelectorAll('[data-facets-form]').forEach(function (form) {
      if (form.dataset.initialised === 'true') return;
      form.dataset.initialised = 'true';

      var timer = null;
      form.addEventListener('change', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { submitFacets(form); }, 250);
      });
    });

    // Sort dropdown posts the same form.
    (scope || document).querySelectorAll('[data-sort-select]').forEach(function (select) {
      on(select, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        navigate(url.toString());
      });
    });
  }

  function submitFacets(form) {
    var params = new URLSearchParams();
    // Empty price inputs would otherwise emit `filter.v.price.gte=` and Shopify
    // treats that as an active (zero) filter, so drop blank values.
    new FormData(form).forEach(function (value, key) {
      if (typeof value === 'string' && value.trim() === '') return;
      params.append(key, value);
    });
    var url = new URL(window.location.href);
    url.search = params.toString();
    url.searchParams.delete('page');
    navigate(url.toString());
  }

  function navigate(url) {
    var grid = document.querySelector('[data-collection-results]');
    if (!grid) { window.location.href = url; return; }
    grid.setAttribute('aria-busy', 'true');
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-collection-results]', '[data-facets]', '[data-active-facets]', '[data-facets-count]'].forEach(
          function (selector) {
            var fresh = doc.querySelector(selector);
            var current = document.querySelector(selector);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          }
        );
        history.pushState({}, '', url);
        grid.removeAttribute('aria-busy');
        initAll(document);
      })
      .catch(function () { window.location.href = url; });
  }

  on(window, 'popstate', function () { window.location.reload(); });

  /* ============================================================= accordion */
  /* The footer menus collapse only below 768px, matching the reference. */

  function initFooterAccordions(scope) {
    (scope || document).querySelectorAll('[data-footer-accordion]').forEach(function (block) {
      var button = block.querySelector('[data-footer-accordion-toggle]');
      var body = block.querySelector('[data-footer-accordion-body]');
      if (!button || !body) return;

      function sync() {
        var mobile = window.innerWidth < DESKTOP;
        if (!mobile) {
          body.hidden = false;
          button.setAttribute('aria-expanded', 'true');
        } else if (button.dataset.userToggled !== 'true') {
          body.hidden = true;
          button.setAttribute('aria-expanded', 'false');
        }
      }

      if (button.dataset.initialised !== 'true') {
        button.dataset.initialised = 'true';
        on(button, 'click', function () {
          if (window.innerWidth >= DESKTOP) return;
          var expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          button.dataset.userToggled = 'true';
          body.hidden = expanded;
        });
        on(window, 'resize', sync);
      }
      sync();
    });
  }

  /* =========================================================== back to top */

  function initBackToTop() {
    var button = document.querySelector('[data-back-to-top]');
    if (!button) return;
    function toggle() { button.classList.toggle('is-visible', window.pageYOffset > 400); }
    on(window, 'scroll', toggle, { passive: true });
    on(button, 'click', function () {
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });
    toggle();
  }

  /* ============================================================== reveal */

  function initReveal(scope) {
    if (!document.body.classList.contains('animate-reveal')) return;
    var items = (scope || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!items.length || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach(function (item) { observer.observe(item); });
  }

  /* ========================================================= video reel */

  // Reels-style rail of real product videos (see sections/video-discovery.liquid).
  // Every card's video/price/link is server-rendered from real Shopify product
  // data; this only adds interaction: drag-to-scroll, viewport-based autoplay
  // (only the visible card plays, to keep bandwidth/CPU sane), per-card
  // play/mute toggles, and the click-to-open product modal. The modal fetches
  // /products/{handle}.js on open so price/stock are always live, and reuses
  // the theme's existing addToCart/cartRequest — nothing about cart or
  // checkout is reimplemented.

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function initVideoRailDrag(scope) {
    (scope || document).querySelectorAll('[data-video-rail]').forEach(function (rail) {
      if (rail.dataset.dragInit === 'true') return;
      rail.dataset.dragInit = 'true';

      var isDown = false;
      var startX = 0;
      var scrollStart = 0;
      var moved = 0;

      // Deliberately no setPointerCapture here: capturing on the rail
      // hijacks where the eventual click event resolves, which broke the
      // card's open-modal click for ordinary (non-drag) clicks. Tracking
      // move/up on window instead keeps a real click's target untouched
      // while still supporting drag-past-the-rail-edge.
      on(rail, 'pointerdown', function (event) {
        if (event.pointerType === 'touch') return; // native touch scrolling handles mobile swipe
        isDown = true;
        moved = 0;
        startX = event.clientX;
        scrollStart = rail.scrollLeft;
        rail.classList.add('is-dragging');
      });
      on(window, 'pointermove', function (event) {
        if (!isDown) return;
        var dx = event.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        rail.scrollLeft = scrollStart - dx;
      });
      function endDrag() {
        isDown = false;
        rail.classList.remove('is-dragging');
      }
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);

      // A real drag shouldn't also fire the card's open-modal click.
      rail.addEventListener(
        'click',
        function (event) {
          if (moved > 6) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    });
  }

  function initVideoRailAutoplay(scope) {
    (scope || document).querySelectorAll('[data-video-rail]').forEach(function (rail) {
      if (rail.dataset.autoplayInit === 'true') return;
      rail.dataset.autoplayInit = 'true';

      var videos = Array.prototype.slice.call(rail.querySelectorAll('[data-video-el]'));
      if (!videos.length) return;

      if (rail.dataset.autoplay !== 'true' || !('IntersectionObserver' in window)) return;

      var current = null;
      var observer = new IntersectionObserver(
        function (entries) {
          var best = null;
          var bestRatio = 0;
          entries.forEach(function (entry) {
            if (entry.intersectionRatio > bestRatio) {
              bestRatio = entry.intersectionRatio;
              best = entry.target;
            }
          });
          if (best && bestRatio > 0.6 && best !== current) {
            if (current) current.pause();
            current = best;
            if (current.dataset.userPaused !== 'true') {
              current.play().catch(function () {
                /* autoplay blocked (e.g. mobile data-saver) — poster stays visible */
              });
            }
          } else if (bestRatio < 0.2 && current) {
            current.pause();
            current = null;
          }
        },
        { root: rail, threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] }
      );
      videos.forEach(function (video) { observer.observe(video); });
    });
  }

  function initVideoCardControls(scope) {
    (scope || document).querySelectorAll('.video-card').forEach(function (card) {
      if (card.dataset.controlsInit === 'true') return;
      card.dataset.controlsInit = 'true';

      var video = card.querySelector('[data-video-el]');
      var playToggle = card.querySelector('[data-video-play-toggle]');
      var muteToggle = card.querySelector('[data-video-mute-toggle]');
      if (!video) return;

      on(video, 'play', function () { if (playToggle) playToggle.classList.remove('is-paused'); });
      on(video, 'pause', function () { if (playToggle) playToggle.classList.add('is-paused'); });

      on(playToggle, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (video.paused) {
          video.dataset.userPaused = 'false';
          video.play().catch(function () {});
        } else {
          video.dataset.userPaused = 'true';
          video.pause();
        }
      });

      on(muteToggle, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        video.muted = !video.muted;
        muteToggle.classList.toggle('is-unmuted', !video.muted);
      });
    });
  }

  /* ------------------------------------------------------- video modal */

  function renderVideoModalInfo(product, sectionEl) {
    var variants = product.variants || [];
    var hasRealOptions = product.options && product.options.length > 0 && !(product.options.length === 1 && product.options[0].name === 'Title');
    var onSale = variants[0] && variants[0].compare_at_price && variants[0].compare_at_price > variants[0].price;

    var badgeHtml = '';
    if (onSale) {
      var saved = Math.round(((variants[0].compare_at_price - variants[0].price) / variants[0].compare_at_price) * 100);
      badgeHtml = '<span class="video-modal__badge">%' + saved + ' ' + escapeHtml(strings.onSale || '') + '</span>';
    }

    var optionsHtml = '';
    if (hasRealOptions) {
      product.options.forEach(function (option, optionIndex) {
        optionsHtml +=
          '<fieldset class="variant-option" data-modal-option-index="' + optionIndex + '">' +
          '<legend class="variant-option__label">' + escapeHtml(option.name) + '</legend>' +
          '<div class="variant-option__values">' +
          option.values
            .map(function (value, valueIndex) {
              var inputId = 'ModalOption-' + optionIndex + '-' + valueIndex;
              var checked = valueIndex === 0 ? ' checked' : '';
              return (
                '<label class="variant-pill" for="' + inputId + '">' +
                '<input type="radio" id="' + inputId + '" name="modal-option-' + optionIndex + '" value="' +
                escapeHtml(value) + '"' + checked + '>' +
                '<span>' + escapeHtml(value) + '</span>' +
                '</label>'
              );
            })
            .join('') +
          '</div></fieldset>';
      });
    }

    return (
      '<h2 class="video-modal__title">' + escapeHtml(product.title) + '</h2>' +
      badgeHtml +
      '<div class="video-modal__price" data-modal-price></div>' +
      '<p class="video-modal__stock" data-modal-stock></p>' +
      optionsHtml +
      '<div class="video-modal__quantity">' +
      '<label class="field__label" for="ModalQuantity">' + escapeHtml(strings.quantity || '') + '</label>' +
      '<input type="number" id="ModalQuantity" class="field__input" value="1" min="1" data-modal-quantity style="width:80px;">' +
      '</div>' +
      '<div class="video-modal__actions">' +
      '<button type="button" class="btn" data-modal-add-to-cart>' + escapeHtml(strings.addToCart || '') + '</button>' +
      '<button type="button" class="btn btn--secondary" data-modal-buy-now>' + escapeHtml(strings.buyNow || '') + '</button>' +
      '<a class="link-underline" href="' + product.url + '" data-modal-view-product>' + escapeHtml(strings.viewProduct || '') + '</a>' +
      '</div>'
    );
  }

  function wireVideoModalInfo(modal, product) {
    var priceEl = modal.querySelector('[data-modal-price]');
    var stockEl = modal.querySelector('[data-modal-stock]');
    var addBtn = modal.querySelector('[data-modal-add-to-cart]');
    var buyBtn = modal.querySelector('[data-modal-buy-now]');
    var qtyInput = modal.querySelector('[data-modal-quantity]');
    var optionGroups = modal.querySelectorAll('[data-modal-option-index]');

    function findMatch() {
      if (!optionGroups.length) return product.variants[0];
      var chosen = Array.prototype.map.call(optionGroups, function (group) {
        var checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
      return product.variants.find(function (variant) {
        return variant.options.every(function (opt, i) { return opt === chosen[i]; });
      });
    }

    function update() {
      var match = findMatch();
      if (priceEl) {
        var html = '<span class="video-modal__price-current">' + formatMoney(match ? match.price : product.price) + '</span>';
        if (match && match.compare_at_price && match.compare_at_price > match.price) {
          html += '<s class="video-modal__price-compare">' + formatMoney(match.compare_at_price) + '</s>';
        }
        priceEl.innerHTML = html;
      }
      if (stockEl) {
        stockEl.textContent = match && match.available ? (strings.inStock || '') : (strings.soldOut || '');
      }
      var disabled = !match || !match.available;
      if (addBtn) addBtn.disabled = disabled;
      if (buyBtn) buyBtn.disabled = disabled;
      modal.dataset.selectedVariantId = match ? match.id : '';
    }

    optionGroups.forEach(function (group) { on(group, 'change', update); });
    update();

    on(addBtn, 'click', function () {
      var id = modal.dataset.selectedVariantId;
      if (!id) return;
      var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
      // Close first: the video modal sits above the cart drawer, so leaving
      // it open would hide the drawer addToCart is about to open.
      closeVideoModal(modal);
      addToCart(id, qty, addBtn);
    });

    on(buyBtn, 'click', function () {
      var id = modal.dataset.selectedVariantId;
      if (!id) return;
      var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
      buyBtn.classList.add('is-loading');
      cartRequest(routes.cartAdd, { items: [{ id: id, quantity: qty }] })
        .then(function () { window.location.href = '/checkout'; })
        .catch(function (err) {
          buyBtn.classList.remove('is-loading');
          window.alert((err && err.description) || strings.cartError);
        });
    });
  }

  function openVideoModal(modal, card) {
    var opener = card.querySelector('[data-video-handle]');
    var handle = opener ? opener.getAttribute('data-video-handle') : null;
    var cardVideo = card.querySelector('[data-video-el]');
    var modalVideo = modal.querySelector('[data-modal-video]');
    var infoEl = modal.querySelector('[data-modal-info]');
    var playToggle = modal.querySelector('[data-modal-play-toggle]');
    var muteToggle = modal.querySelector('[data-modal-mute-toggle]');
    if (!handle || !modalVideo) return;

    // Pause whichever rail card is currently autoplaying so audio doesn't
    // double up once the modal's own copy starts.
    if (cardVideo) cardVideo.pause();

    modalVideo.innerHTML = cardVideo ? cardVideo.innerHTML : '';
    modalVideo.muted = true;
    modalVideo.load();
    modalVideo.play().catch(function () {});
    if (playToggle) playToggle.classList.remove('is-paused');
    if (muteToggle) muteToggle.classList.remove('is-unmuted');

    if (infoEl) infoEl.innerHTML = '<p class="video-modal__stock">' + escapeHtml(strings.loading || '…') + '</p>';

    modal.hidden = false;
    modal.dataset.opener = card.id || '';
    lockScroll(true);
    document.dispatchEvent(new CustomEvent('theme:video-modal:open'));

    var closeBtn = modal.querySelector('[data-video-modal-close]');
    if (closeBtn) window.setTimeout(function () { closeBtn.focus(); }, 60);

    fetch('/products/' + handle + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('not found');
        return response.json();
      })
      .then(function (product) {
        if (modal.hidden) return; // closed before the fetch resolved
        if (infoEl) {
          infoEl.innerHTML = renderVideoModalInfo(product, modal);
          wireVideoModalInfo(modal, product);
        }
      })
      .catch(function () {
        if (infoEl) infoEl.innerHTML = '<p class="video-modal__stock">' + escapeHtml(strings.searchNoResults || '') + '</p>';
      });
  }

  function closeVideoModal(modal) {
    var video = modal.querySelector('[data-modal-video]');
    if (video) { video.pause(); video.removeAttribute('src'); video.innerHTML = ''; }
    modal.hidden = true;
    lockScroll(false);
    var opener = modal.dataset.opener ? document.getElementById(modal.dataset.opener) : null;
    if (opener) opener.focus();
    document.dispatchEvent(new CustomEvent('theme:video-modal:close'));
  }

  function initVideoModal(scope) {
    (scope || document).querySelectorAll('[data-video-modal]').forEach(function (modal) {
      if (modal.dataset.modalInit === 'true') return;
      modal.dataset.modalInit = 'true';

      var section = modal.closest('.shopify-section') || document;

      section.addEventListener('click', function (event) {
        var opener = event.target.closest('[data-video-card-open]');
        if (!opener) return;
        event.preventDefault();
        openVideoModal(modal, opener.closest('.video-card'));
      });

      modal.querySelectorAll('[data-video-modal-close]').forEach(function (closer) {
        on(closer, 'click', function () { closeVideoModal(modal); });
      });

      var modalVideo = modal.querySelector('[data-modal-video]');
      var playToggle = modal.querySelector('[data-modal-play-toggle]');
      var muteToggle = modal.querySelector('[data-modal-mute-toggle]');
      if (modalVideo) {
        on(modalVideo, 'play', function () { if (playToggle) playToggle.classList.remove('is-paused'); });
        on(modalVideo, 'pause', function () { if (playToggle) playToggle.classList.add('is-paused'); });
      }
      on(playToggle, 'click', function () {
        if (!modalVideo) return;
        if (modalVideo.paused) modalVideo.play().catch(function () {});
        else modalVideo.pause();
      });
      on(muteToggle, 'click', function () {
        if (!modalVideo) return;
        modalVideo.muted = !modalVideo.muted;
        muteToggle.classList.toggle('is-unmuted', !modalVideo.muted);
      });
    });

    document.addEventListener('keydown', function (event) {
      var openModal = document.querySelector('[data-video-modal]:not([hidden])');
      if (!openModal) return;
      if (event.key === 'Escape') closeVideoModal(openModal);
      if (event.key === 'Tab') {
        var dialog = openModal.querySelector('[data-video-modal-dialog]');
        if (dialog) trapFocus(dialog, event);
      }
    });
  }

  function initVideoDiscovery(scope) {
    initVideoRailDrag(scope);
    initVideoRailAutoplay(scope);
    initVideoCardControls(scope);
    initVideoModal(scope);
  }

  /* ==================================================== turkish uppercase */

  // CSS text-transform: uppercase always uses the default Unicode case
  // mapping, which turns "i" into dotless "I" instead of Turkish "İ" — CSS
  // has no reliable way to make this locale-aware. We re-uppercase the
  // rendered text of uppercase-styled elements with a Turkish-aware pass and
  // disable the CSS transform on them, so the letterforms are always correct
  // regardless of source (theme content, translations, or merchant-entered
  // Theme Editor text).
  function fixTurkishUppercase(root) {
    if (document.documentElement.lang !== 'tr') return;
    (root || document.body).querySelectorAll('*').forEach(function (el) {
      if (el.children.length || el.dataset.trFixed) return;
      var text = el.textContent;
      if (!text || !/[a-zçğıöşü]/i.test(text)) return;
      if (getComputedStyle(el).textTransform !== 'uppercase') return;
      el.textContent = text.toLocaleUpperCase('tr-TR');
      el.style.textTransform = 'none';
      el.dataset.trFixed = 'true';
    });
  }

  function initTurkishUppercaseWatcher() {
    if (document.documentElement.lang !== 'tr' || !('MutationObserver' in window)) return;
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        fixTurkishUppercase(document.body);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ============================================================ wishlist
     Wires the theme's own product-card / PDP heart buttons to the real
     Wishlist Plus app (window.wlHelpers / window.apiHandlerWishlist,
     exposed by the app's own "app" embed block). No local storage, no
     second wishlist system — this only talks to the app's real endpoints,
     mirroring the request shapes used by its own wishlist.js /
     wishlist-collection.js scripts. */

  var WISHLIST_POLL_INTERVAL = 200;
  var WISHLIST_POLL_MAX_TRIES = 25; // ~5s of retrying for the app embed to load

  function setWishlistBtnState(btn, isWishlisted) {
    btn.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false');
  }

  function optimisticWishlistToggle(btn, wl) {
    var isAdding = btn.getAttribute('aria-pressed') !== 'true';
    setWishlistBtnState(btn, isAdding);

    var payload = Object.assign(
      {
        product_id: parseInt(btn.dataset.productId, 10),
        variant_id: parseInt(btn.dataset.variantId, 10) || undefined
      },
      wl.getIdentityPayload()
    );
    if (isAdding) payload.product_handle = btn.dataset.productHandle || '';

    window.apiHandlerWishlist(isAdding ? '/add-wishlist' : '/remove-wishlist', {
      method: 'POST',
      body: JSON.stringify(payload)
    }).then(function (data) {
      if (data && data.success) {
        if (wl.handleGuestResponse) wl.handleGuestResponse(data);
        if (wl.refreshHeaderWishlistCount) wl.refreshHeaderWishlistCount();
      } else {
        setWishlistBtnState(btn, !isAdding);
      }
    }).catch(function () {
      setWishlistBtnState(btn, !isAdding);
    });
  }

  function wireWishlistButton(btn, wl) {
    if (btn.dataset.wishlistWired === 'true') return;
    btn.dataset.wishlistWired = 'true';

    var productId = parseInt(btn.dataset.productId, 10);

    if (productId) {
      wl.checkProductWishlistStatus(productId, parseInt(btn.dataset.variantId, 10) || undefined)
        .then(function (status) {
          setWishlistBtnState(btn, status.isWishlisted);
          btn.dataset.wishlistIds = JSON.stringify(status.wishlistIds || []);
        })
        .catch(function () {});
    }

    // The app's own multi-wishlist modal (handleMultiWishlistClick) sets
    // data-added on the button when the customer confirms a change there —
    // mirror that into our aria-pressed/icon state instead of duplicating
    // its modal logic.
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        setWishlistBtnState(btn, btn.getAttribute('data-added') === 'true');
      }).observe(btn, { attributes: true, attributeFilter: ['data-added'] });
    }

    btn.addEventListener('click', function () {
      if (wl.isGuest && !wl.guestWishlistEnabled) {
        wl.showLoginPopup();
        return;
      }

      if (wl.multipleWishlistsEnabled) {
        var cachedStatus = null;
        try {
          cachedStatus = {
            isWishlisted: btn.getAttribute('aria-pressed') === 'true',
            wishlistIds: JSON.parse(btn.dataset.wishlistIds || '[]')
          };
        } catch (e) { cachedStatus = null; }

        wl.handleMultiWishlistClick(
          btn,
          productId,
          parseInt(btn.dataset.variantId, 10) || undefined,
          btn.dataset.productHandle || '',
          cachedStatus
        );
      } else {
        optimisticWishlistToggle(btn, wl);
      }
    });
  }

  function wireWishlistVariantSync(wl) {
    if (document.documentElement.dataset.wishlistVariantSyncWired === 'true') return;
    document.documentElement.dataset.wishlistVariantSyncWired = 'true';

    var picker = document.querySelector('[data-variant-picker]');
    var pdpBtn = document.querySelector('.product__wishlist[data-wishlist-btn]');
    if (!picker || !pdpBtn) return;

    picker.addEventListener('change', function () {
      var form = picker.closest('form');
      var idInput = form ? form.querySelector('[data-variant-id]') : null;
      var variantId = idInput ? parseInt(idInput.value, 10) : null;
      if (!variantId) return;

      pdpBtn.dataset.variantId = String(variantId);

      var productId = parseInt(pdpBtn.dataset.productId, 10);
      if (!productId) return;

      wl.checkProductWishlistStatus(productId, variantId).then(function (status) {
        setWishlistBtnState(pdpBtn, status.isWishlisted);
        pdpBtn.dataset.wishlistIds = JSON.stringify(status.wishlistIds || []);
      }).catch(function () {});
    });
  }

  function initWishlistButtons(scope, tries) {
    var buttons = (scope || document).querySelectorAll('[data-wishlist-btn]');
    if (buttons.length === 0) return;

    var wl = window.wlHelpers;
    if (!wl || typeof window.apiHandlerWishlist !== 'function') {
      tries = (tries || 0) + 1;
      if (tries > WISHLIST_POLL_MAX_TRIES) return;
      setTimeout(function () { initWishlistButtons(scope, tries); }, WISHLIST_POLL_INTERVAL);
      return;
    }

    buttons.forEach(function (btn) { wireWishlistButton(btn, wl); });
    wireWishlistVariantSync(wl);
  }

  /* ================================================================= init */

  function initAll(scope) {
    initHeroSliders(scope);
    initRails(scope);
    initHotspots(scope);
    initGalleries(scope);
    initProductVideoFullscreenFix(scope);
    initVariantPickers(scope);
    initPredictiveSearch(scope);
    initFacets(scope);
    initFooterAccordions(scope);
    initReveal(scope);
    initVideoDiscovery(scope);
    initWishlistButtons(scope);
    fixTurkishUppercase(scope);
  }

  function boot() {
    initStickyHeader();
    initMobileNav();
    initBackToTop();
    initAll(document);
    initTurkishUppercaseWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme Editor lifecycle — re-initialise re-rendered sections. */
  document.addEventListener('shopify:section:load', function (event) {
    initStickyHeader();
    initMobileNav();
    initAll(event.target);
  });
  document.addEventListener('shopify:section:select', function (event) {
    var drawerId = event.target.querySelector('[data-drawer-autoopen]');
    if (drawerId) Drawer.open(drawerId.getAttribute('data-drawer-autoopen'));
  });
  document.addEventListener('shopify:section:deselect', function () { Drawer.closeAll(); });
})();
